import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, LayoutDashboard } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [itemType, setItemType] = useState("tier");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0, cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      if (attempts > 12) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setItemType(data.item_type || "tier");
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setStatus("error"); return; }
      } catch (e) {}
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FAF9F6] grain flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="card-pop bg-white p-10 w-full max-w-md text-center" data-testid="payment-success-card">
        {status === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-violet-600 animate-spin mx-auto mb-5" />
            <h1 className="font-display text-2xl font-extrabold mb-2">Confirming your order…</h1>
            <p className="text-slate-600 text-sm">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-lime-300 border-2 border-[#111114] flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-9 h-9 text-[#111114]" />
            </div>
            <h1 className="font-display text-3xl font-extrabold mb-2">{itemType === "product" ? "Purchase complete! 🎉" : "You're subscribed! 🎉"}</h1>
            <p className="text-slate-600 mb-8">{itemType === "product" ? "Your download is ready in your dashboard." : "Your membership is now active. Enjoy!"}</p>
            <Link to="/member"><Button className="bg-violet-600 text-white btn-pop rounded-full h-11 px-8 font-bold" data-testid="go-member-dashboard"><LayoutDashboard className="w-4 h-4 mr-2" /> Go to my dashboard</Button></Link>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-rose-300 border-2 border-[#111114] flex items-center justify-center mx-auto mb-5">
              <XCircle className="w-9 h-9 text-[#111114]" />
            </div>
            <h1 className="font-display text-2xl font-extrabold mb-2">{status === "timeout" ? "Still processing" : "Something went wrong"}</h1>
            <p className="text-slate-600 mb-8">{status === "timeout" ? "This is taking longer than expected. Check your dashboard shortly." : "We couldn't confirm your payment. If you were charged, contact the creator."}</p>
            <Link to="/member"><Button className="bg-white text-[#111114] btn-pop rounded-full h-11 px-8 font-bold">Go to dashboard</Button></Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
